"use client";

import React from "react";
import { Intersection } from "@/components/common/intersection";
import { useMediaQuery } from "@/components/common/use-media-query";
import { Container } from "@/components/ui/container";
import { Eye, Trophy, Users } from "lucide-react";

export const HowItWorksSection = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const threshold = isMobile ? 0.2 : 0.7;
  const transition = "transition-all duration-1000";

  const steps = [
    {
      number: 1,
      icon: Eye,
      title: "Browse Free",
      subtitle: "Explore courses & community",
      description:
        "Create your free account and browse golf courses near you. View course info, reviews, and ratings. Explore what the golf community has to offer before committing.",
      tierLabel: "Free Tier",
      tierColor: "text-slate-400",
    },
    {
      number: 2,
      icon: Trophy,
      title: "Become a Member",
      subtitle: "$8.99/month • Challenge & connect",
      description:
        "Upgrade to Member to unlock challenges, messaging, and the social feed. Send up to 5 challenges per week, post to your profile, tag courses, and join unlimited leagues. This is where the fun begins.",
      tierLabel: "Member",
      tierColor: "text-emerald-500",
    },
    {
      number: 3,
      icon: Users,
      title: "Build Your League",
      subtitle: "Create & manage your golf community",
      description:
        "Ready to organize? Create your own league with custom branding, event scheduling, standings, and member management. Bring your golf community together on Local Links.",
      tierLabel: "League Owner",
      tierColor: "text-amber-500",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="w-full py-12 md:py-24 lg:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg2.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Light overlay for readability */}
      <div className="absolute inset-0 bg-muted/90" />
      <Container className="relative z-10">
        <h2 className="text-3xl font-bold tracking-tight text-center sm:text-4xl mb-4 px-4 md:px-0">
          Simple Pricing, Clear Value
        </h2>
        <p className="text-center text-muted-foreground mb-12 md:mb-16 max-w-2xl mx-auto">
          Start free, upgrade when you're ready to compete
        </p>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
          {steps.map((step) => (
            <Intersection key={step.number} threshold={threshold}>
              {({ inView, ref }) => (
                <div
                  ref={ref}
                  className={`flex flex-col items-center ${transition} bg-background p-4 sm:p-6 rounded-lg shadow-lg hover:shadow-xl text-center border-2 ${
                    inView
                      ? step.number === 2
                        ? "border-emerald-500 shadow-2xl z-10"
                        : step.number === 3
                          ? "border-amber-500 shadow-2xl z-10"
                          : "border-primary shadow-2xl z-10"
                      : "border-transparent"
                  }`}
                  style={{ minHeight: 320 }}
                >
                  <div
                    className={`rounded-full w-16 h-16 flex items-center justify-center mb-4 ${transition} ${
                      inView
                        ? step.number === 2
                          ? "bg-emerald-500 text-white"
                          : step.number === 3
                            ? "bg-amber-500 text-white"
                            : "bg-primary text-primary-foreground"
                        : "bg-gray-300 text-gray-700"
                    }`}
                  >
                    <step.icon className="h-8 w-8" />
                  </div>

                  <span
                    className={`text-xs font-medium mb-2 ${step.tierColor}`}
                  >
                    {step.tierLabel}
                  </span>

                  <h3
                    className={
                      inView
                        ? "text-2xl font-bold"
                        : "text-xl font-semibold text-gray-700"
                    }
                  >
                    {step.title}
                  </h3>

                  <p
                    className={`text-sm mb-3 ${
                      step.number === 2
                        ? "text-emerald-500"
                        : step.number === 3
                          ? "text-amber-500"
                          : "text-muted-foreground"
                    }`}
                  >
                    {step.subtitle}
                  </p>

                  <p
                    className={
                      inView ? "text-muted-foreground" : "text-gray-600"
                    }
                  >
                    {step.description}
                  </p>
                </div>
              )}
            </Intersection>
          ))}
        </div>

        {/* Additional context for managers */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background border">
            <Users className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              Running a league or course?
              <span className="text-primary ml-1 font-medium">
                Manager roles available separately
              </span>
            </span>
          </div>
        </div>
      </Container>
    </section>
  );
};
